import type { FUSportsMatch } from "fansunited-data-layer";

export interface ScoreDisplayResult {
    scoreOne: string;
    scoreTwo: string;
    statusLabel: string;
    statusTooltip: string;
}

export interface ScoreDisplayLabels {
    ft: string;
    ftFull: string;
    aet: string;
    aetFull: string;
    ap: string;
    apFull: string;
}

/**
 * Compute score display based on match status.
 * Handles penalty shootouts, extra time, and regular finishes.
 *
 * When `winner` is provided (from the Sports Events API `event.winner`),
 * its `reason` string takes precedence over the status code for choosing
 * between FT / AET / AP badges. This surfaces the API's explicit decision
 * reason instead of inferring it from the status code alone.
 */
export function computeScoreDisplay(
    score: FUSportsMatch["score"],
    statusCode: string,
    labels: ScoreDisplayLabels,
    winner?: { competitorId: string; reason: string },
): ScoreDisplayResult {
    const breakdown = score?.breakdown;
    const penaltyScore = breakdown?.penaltyShootout;
    const regularTimeScore = breakdown?.regularTime;

    // Determine if match ended in penalties or extra time. Prefer the
    // explicit API reason when supplied; fall back to status-code inference.
    const reason = winner?.reason;
    const isAfterPenalties = reason === "PENALTIES" || statusCode === "finished_after_penalties";
    const isAfterExtraTime = reason === "EXTRA_TIME" || statusCode === "finished_after_extra_time";

    // Get the main score (FT/ET without penalties)
    let scoreOne = score?.competitorOne || "0";
    let scoreTwo = score?.competitorTwo || "0";

    if (isAfterPenalties && regularTimeScore) {
        scoreOne = regularTimeScore.competitorOne || "0";
        scoreTwo = regularTimeScore.competitorTwo || "0";
    }

    // Determine winner for penalty shootout asterisk
    let competitorOneWins = false;
    let competitorTwoWins = false;

    if (isAfterPenalties && penaltyScore) {
        const penOne = parseInt(penaltyScore.competitorOne || "0");
        const penTwo = parseInt(penaltyScore.competitorTwo || "0");
        competitorOneWins = penOne > penTwo;
        competitorTwoWins = penTwo > penOne;
    }

    // Format scores with asterisk for penalty winner
    const displayScoreOne = isAfterPenalties && competitorOneWins ? `*${scoreOne}` : scoreOne;
    const displayScoreTwo = isAfterPenalties && competitorTwoWins ? `${scoreTwo}*` : scoreTwo;

    // Determine status label and tooltip
    let statusLabel = labels.ft; // Default: FT
    let statusTooltip = labels.ftFull;

    if (isAfterExtraTime) {
        statusLabel = labels.aet;
        statusTooltip = labels.aetFull;
    } else if (isAfterPenalties && penaltyScore) {
        statusLabel = `${labels.ap}, ${penaltyScore.competitorOne}-${penaltyScore.competitorTwo}`;
        statusTooltip = labels.apFull;
    }

    return {
        scoreOne: displayScoreOne,
        scoreTwo: displayScoreTwo,
        statusLabel,
        statusTooltip,
    };
}

